/**
 * Shared native secure-storage configuration (Android Keystore / iOS Keychain).
 *
 * Tokens and credentials must not sync to iCloud or migrate via device backups,
 * so we disable synchronize and use whenUnlockedThisDeviceOnly everywhere.
 */
import { isNative } from './platform'
import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage'

/** iOS Keychain access — device-only, no backup/transfer. */
export const NATIVE_KEYCHAIN_ACCESS = KeychainAccess.whenUnlockedThisDeviceOnly

let _configured = false

/**
 * One-time OS store configuration. Safe to call multiple times.
 * @returns {Promise<void>}
 */
export async function configureNativeSecureStorage() {
  if (!isNative() || _configured) return
  _configured = true
  try {
    await SecureStorage.setSynchronize(false)
    await SecureStorage.setDefaultKeychainAccess(NATIVE_KEYCHAIN_ACCESS)
  } catch (e) {
    console.warn('[SecureStorage] configure failed:', e?.message || e)
  }
}

/**
 * Persist a string value with the strict Keychain/Keystore policy.
 * @param {string} key
 * @param {string} value
 */
export async function nativeSecureSetString(key, value) {
  await configureNativeSecureStorage()
  await SecureStorage.set(key, String(value), true, false, NATIVE_KEYCHAIN_ACCESS)
}

/**
 * Persist structured data (objects) with the strict Keychain/Keystore policy.
 * @param {string} key
 * @param {import('@aparajita/capacitor-secure-storage').DataType} data
 */
export async function nativeSecureSetData(key, data) {
  await configureNativeSecureStorage()
  await SecureStorage.set(key, data, true, false, NATIVE_KEYCHAIN_ACCESS)
}
